import posthog from "posthog-js";
import { HandoffReturnNotice } from "./components/HandoffReturnNotice";
import { useEffect, useRef } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import DraftsRoute from "./components/DraftsRoute";
import { CreatePost } from "./components/CreatePost";
import OnboardingPage from "./pages/OnboardingPage";
import OnboardingPreviewComparison from "./pages/OnboardingPreviewComparison";
import AnalyticsNoticePreview from "./pages/AnalyticsNoticePreview";
import DashboardLayout from "./components/DashboardLayout";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { FeedbackForm } from "./components/FeedbackForm";
import DevFacebookLogin from "./components/DevFacebookLogin";
import FacebookPageSelector from "./components/FacebookPageSelector";
import LinkedInPageSelector from "./components/LinkedInPageSelector";
import SettingsPage from "./pages/SettingsPage";
import { PrivateRoute } from "./components/PrivateRoute";
import { SvgFilterDefinitions } from "./components/SvgFilterDefinitions";
import { Toaster } from "./components/ui/toaster";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { StripeProvider } from "./contexts/StripeContext";
import AdminDashboard from "./pages/AdminDashboard";
import AeoVisibilityPage from "./pages/AeoVisibilityPage";
import BrandKitsPage from "./pages/BrandKitsPage";
import FinishSignIn from "./pages/FinishSignIn";
import GenerateHomePage from "./pages/GenerateHomePage";
import { IdeasRoute } from "./pages/PrivateIdeaPage";
import SourceDocumentPage from "./pages/SourceDocumentPage";
import InvitePage from "./pages/InvitePage";
import Login from "./pages/Login";
import LoginWithPassword from "./pages/LoginWithPassword";
import AnalyticsPage from "./pages/AnalyticsPage";
import PostsPage from "./pages/PostsPage";
import UpgradePage from "./pages/UpgradePage";
import WorkspacePeoplePage from "./pages/WorkspacePeoplePage";
import { SharePage } from "./pages/SharePage";
import { Demo as RemotionDemo } from "./remotion-prototype/Demo";
import { draftsBackgroundLocation } from "./lib/editorBackgroundRoute";
import { postsSearchForView } from "./lib/postsView";

function LegacyStudioRedirect() {
  const location = useLocation();
  return <Navigate to={`/generate${location.search}${location.hash}`} state={location.state} replace />;
}

function LegacyPlanRedirect() {
  const location = useLocation();
  return <Navigate to={`/ideas${location.search}${location.hash}`} state={location.state} replace />;
}

const workspacesEnabled = process.env.REACT_APP_WORKSPACES_ENABLED === "true";

/**
 * Captures $pageview events after Firebase auth resolves so every pageview
 * is attributed to the correct identity. Handles both the initial page load
 * and subsequent SPA navigations. Must be rendered inside both <Router> and
 * <AuthProvider>.
 */
function PageviewTracker() {
  const location = useLocation();
  const { firebasePending } = useAuth();
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    // Wait until auth state is known (identify has already run if logged in)
    if (firebasePending) return;
    // Deduplicate same-path captures (StrictMode, re-renders)
    if (location.pathname === previousPath.current) return;
    previousPath.current = location.pathname;
    if (process.env.REACT_APP_POSTHOG_DISABLED !== "true") {
      const safePath =
        location.pathname === "/invite" ? "/invite" : location.pathname;
      posthog.capture("$pageview", {
        $current_url: `${window.location.origin}${safePath}`,
      });
    }
  }, [location.pathname, firebasePending]);

  return null;
}

function WorkspaceFeatureRoute({ children }: { children: React.ReactNode }) {
  const { loading, workspaceFeaturesEnabled } = useAuth();
  if (loading) return null;
  return workspaceFeaturesEnabled ? (
    <>{children}</>
  ) : (
    <Navigate to="/" replace />
  );
}

function WorkspaceOwnerRoute({ children }: { children: React.ReactNode }) {
  const { loading, activeWorkspace } = useAuth();
  if (loading) return null;
  if (activeWorkspace?.role !== "owner") {
    return <Navigate to="/workspace" replace />;
  }
  return <>{children}</>;
}

function CreatePostScreen() {
  return (
    <PrivateRoute>
      <DashboardLayout fullScreen={true}>
        <CreatePost />
      </DashboardLayout>
    </PrivateRoute>
  );
}

function LegacyCalendarRedirect() {
  const location = useLocation();

  return (
    <Navigate
      to={{
        pathname: "/dashboard",
        search: postsSearchForView(location.search, "calendar"),
      }}
      replace
    />
  );
}

function AppRoutes() {
  const location = useLocation();
  const backgroundLocation = draftsBackgroundLocation(
    location.pathname,
    location.state,
  );

  return (
    <>
      <div
        className={backgroundLocation ? "invisible" : undefined}
        aria-hidden={backgroundLocation ? true : undefined}
      >
        <Routes location={backgroundLocation ?? location}>
          <Route path="/onboarding" element={<PrivateRoute><OnboardingPage /></PrivateRoute>} />
          <Route path="/" element={<Navigate to="/generate" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/login-with-password" element={<LoginWithPassword />} />
          <Route path="/finish-signin" element={<FinishSignIn />} />
          <Route
            path="/invite"
            element={
              workspacesEnabled ? <InvitePage /> : <Navigate to="/" replace />
            }
          />
          <Route path="/share/:shareId" element={<SharePage />} />
          <Route path="/shared/:shareId" element={<SharePage />} />
          <Route path="/remotion-demo" element={<RemotionDemo />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <DashboardLayout children={<PostsPage />} />
              </PrivateRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <PrivateRoute>
                <DashboardLayout children={<AnalyticsPage />} />
              </PrivateRoute>
            }
          />
          <Route
            path="/generate"
            element={
              <PrivateRoute>
                <DashboardLayout>
                  <GenerateHomePage />
                </DashboardLayout>
              </PrivateRoute>
            }
          />
          <Route
            path="/ideas"
            element={
              <PrivateRoute>
                <IdeasRoute />
              </PrivateRoute>
            }
          />
          <Route path="/studio" element={<LegacyStudioRedirect />} />
          <Route
            path="/documents/:documentId"
            element={
              <PrivateRoute>
                <SourceDocumentPage />
              </PrivateRoute>
            }
          />
          <Route path="/create-post" element={<CreatePostScreen />} />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <DashboardLayout children={<SettingsPage />} />
              </PrivateRoute>
            }
          />
          {process.env.NODE_ENV === "development" && (
            <Route
              path="/settings/dev-facebook-login"
              element={
                <PrivateRoute>
                  <DevFacebookLogin />
                </PrivateRoute>
              }
            />
          )}
          <Route
            path="/settings/facebook-pages"
            element={
              <PrivateRoute>
                <FacebookPageSelector />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings/linkedin-pages"
            element={
              <PrivateRoute>
                <LinkedInPageSelector />
              </PrivateRoute>
            }
          />
          <Route path="/plan" element={<LegacyPlanRedirect />} />
          <Route
            path="/calendar"
            element={
              <PrivateRoute>
                <LegacyCalendarRedirect />
              </PrivateRoute>
            }
          />
          <Route
            path="/created"
            element={
              <PrivateRoute>
                <DraftsRoute />
              </PrivateRoute>
            }
          />
          <Route
            path="/feedback"
            element={
              <PrivateRoute>
                <DashboardLayout children={<FeedbackForm />} />
              </PrivateRoute>
            }
          />
          <Route
            path="/brand-kits"
            element={
              <PrivateRoute>
                <DashboardLayout children={<BrandKitsPage />} />
              </PrivateRoute>
            }
          />
          <Route
            path="/upgrade"
            element={
              <PrivateRoute>
                <WorkspaceOwnerRoute>
                  <UpgradePage />
                </WorkspaceOwnerRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/workspace"
            element={
              workspacesEnabled ? (
                <PrivateRoute>
                  <WorkspaceFeatureRoute>
                    <DashboardLayout children={<WorkspacePeoplePage />} />
                  </WorkspaceFeatureRoute>
                </PrivateRoute>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/workspace/people"
            element={<Navigate to="/workspace" replace />}
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute>
                <DashboardLayout children={<AdminDashboard />} />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/aeo"
            element={
              <PrivateRoute>
                <DashboardLayout children={<AeoVisibilityPage />} />
              </PrivateRoute>
            }
          />
        </Routes>
      </div>

      {backgroundLocation && (
        <div className="fixed inset-0 z-50 bg-background">
          <Routes location={location}>
            <Route path="/create-post" element={<CreatePostScreen />} />
          </Routes>
        </div>
      )}
    </>
  );
}

function App() {
  if (process.env.NODE_ENV === "development" && window.location.pathname === "/dev/onboarding-preview") {
    return <OnboardingPreviewComparison />;
  }
  if (process.env.NODE_ENV === "development" && window.location.pathname === "/dev/analytics-notice") {
    return <AnalyticsNoticePreview />;
  }
  return (
    <Router>
      <SvgFilterDefinitions />
      <Toaster />
      <HandoffReturnNotice />
      <AuthProvider>
        <PageviewTracker />
        <StripeProvider>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </StripeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
